import {
  BackgroundJobStatusValue,
  isActiveBackgroundJobStatus,
} from 'src/fetch/backgroundJobs/backgroundJobStatus'
import { BackgroundJobType } from 'types/graphql'

import type { TrackedBackgroundJob } from './types'

export const BACKGROUND_JOB_GENERIC_ERROR_MESSAGE = 'Something went wrong.'

export const PILL_COPY_SEGMENT_SEPARATOR = ' · '

export type BackgroundJobBarVariant = 'inProgress' | 'success' | 'warning' | 'error' | 'cancelled'

export type BackgroundJobBarContent = {
  variant: BackgroundJobBarVariant
  message: string
  description?: string
}

export type AggregateBackgroundJobBarContent = BackgroundJobBarContent & {
  showSpinner: boolean
}

export type PillLocationContext = {
  recordTypeName?: string | null
  viewName?: string | null
}

const variantByBarVariant: Record<BackgroundJobBarVariant, string> = {
  inProgress: 'bg-amber-50 border-amber-200 text-amber-950',
  success: 'bg-green-50 border-green-200 text-green-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-950',
  error: 'bg-red-50 border-red-200 text-red-900',
  cancelled: 'bg-neutral-50 border-neutral-300 text-neutral-600',
}

/** Text color only — dropdown rows mirror prototype menu items (no per-row pill chrome). */
const variantTextByBarVariant: Record<BackgroundJobBarVariant, string> = {
  inProgress: 'text-amber-950',
  success: 'text-green-900',
  warning: 'text-amber-950',
  error: 'text-red-900',
  cancelled: 'text-neutral-600',
}

export const backgroundJobBarVariantClassName = (variant: BackgroundJobBarVariant) =>
  variantByBarVariant[variant]

export const backgroundJobBarVariantTextClassName = (variant: BackgroundJobBarVariant) =>
  variantTextByBarVariant[variant]

const joinPillCopySegments = (segments: Array<string | null | undefined>) =>
  segments
    .filter((segment): segment is string => Boolean(segment))
    .join(PILL_COPY_SEGMENT_SEPARATOR)

/**
 * Dropdown rows include record type only when jobs span multiple modules — on one
 * records list every row shares recordTypeId, so tab name is the disambiguator.
 */
export const shouldIncludeRecordTypeInMultiJobDropdown = (
  trackedJobs: Array<Pick<TrackedBackgroundJob, 'recordTypeId'>>
): boolean => {
  const recordTypeIds = new Set(
    trackedJobs
      .map((trackedJob) => trackedJob.recordTypeId)
      .filter((recordTypeId): recordTypeId is number => recordTypeId != null)
  )
  return recordTypeIds.size > 1
}

/** Saved-tab label segments appended after status copy (record type, then tab name). */
export const getPillLocationSegments = ({
  recordTypeName,
  viewName,
}: PillLocationContext): string[] => {
  const segments: string[] = []
  if (recordTypeName) {
    segments.push(recordTypeName)
  }
  if (viewName) {
    segments.push(viewName)
  }
  return segments
}

export const appendPillLocationContext = (
  message: string,
  location: PillLocationContext
): string => {
  const locationSuffix = joinPillCopySegments(getPillLocationSegments(location))
  return locationSuffix ? joinPillCopySegments([message, locationSuffix]) : message
}

const shouldAppendPillLocationOnSinglePill = (
  job: Pick<TrackedBackgroundJob, 'isInTerminalStatus' | 'status'>
) => job.isInTerminalStatus && job.status !== BackgroundJobStatusValue.Cancelled

/** Applies layout-specific location suffix rules on top of status copy. */
export const getPillRowDisplayMessage = ({
  message,
  layout,
  job,
  shouldIncludeRecordTypeInLocation = true,
}: {
  message: string
  layout: 'dropdown' | 'pill'
  job: Pick<TrackedBackgroundJob, 'isInTerminalStatus' | 'recordTypeName' | 'status' | 'viewName'>
  shouldIncludeRecordTypeInLocation?: boolean
}): string => {
  const location = {
    recordTypeName: shouldIncludeRecordTypeInLocation ? job.recordTypeName : null,
    viewName: job.viewName,
  }

  if (layout === 'dropdown') {
    return appendPillLocationContext(message, location)
  }

  if (shouldAppendPillLocationOnSinglePill(job)) {
    return appendPillLocationContext(message, location)
  }

  return message
}

const isBulkInspectionJobType = (type: BackgroundJobType) =>
  type === 'BulkCancelInspections' ||
  type === 'BulkReassignInspections' ||
  type === 'BulkScheduleInspections'

const getFailedEntityDescription = (type: BackgroundJobType, failed: string): string => {
  if (type === 'BulkScheduleInspections') {
    return `${failed} inspections could not be scheduled.`
  }
  if (type === 'BulkReassignInspections') {
    return `${failed} inspections could not be reassigned.`
  }
  if (type === 'BulkCancelInspections') {
    return `${failed} inspections could not be cancelled.`
  }
  return `${failed} records could not be updated.`
}

const getPartialFailureEntityDescription = (type: BackgroundJobType, failed: string): string =>
  isBulkInspectionJobType(type) ? `${failed} inspections failed.` : `${failed} records failed.`

const getSuccessEntityCountLabel = (type: BackgroundJobType, succeeded: string): string =>
  isBulkInspectionJobType(type) ? `${succeeded} inspections` : `${succeeded} records`

const getCancelledPartialEntityDescription = (type: BackgroundJobType, updated: string): string => {
  if (type === 'BulkScheduleInspections') {
    return `${updated} inspections were scheduled before cancel.`
  }
  if (type === 'BulkReassignInspections') {
    return `${updated} inspections were reassigned before cancel.`
  }
  if (type === 'BulkCancelInspections') {
    return `${updated} inspections were cancelled before cancel.`
  }
  return `${updated} records were updated before cancel.`
}

const getCancelledNoUpdatesEntityDescription = (type: BackgroundJobType): string => {
  if (type === 'BulkScheduleInspections') {
    return 'No inspections were scheduled.'
  }
  if (type === 'BulkReassignInspections') {
    return 'No inspections were reassigned.'
  }
  if (type === 'BulkCancelInspections') {
    return 'No inspections were cancelled.'
  }
  return 'No records were updated.'
}

/** In-progress copy — e.g. "Reassigning record owner · 40/1,234". */
export const getInProgressBarContent = (job: TrackedBackgroundJob): BackgroundJobBarContent => {
  if (job.totalCount === 0) {
    return {
      variant: 'inProgress',
      message: joinPillCopySegments([job.pillActionLabel, 'Starting…']),
    }
  }
  return {
    variant: 'inProgress',
    message: joinPillCopySegments([
      job.pillActionLabel,
      `${job.processedCount.toLocaleString()}/${job.totalCount.toLocaleString()}`,
    ]),
  }
}

export const getCompletionBarContent = (job: TrackedBackgroundJob): BackgroundJobBarContent => {
  const action = job.pillTerminalActionLabel
  const succeeded = job.successCount.toLocaleString()
  const failed = job.errorCount.toLocaleString()

  if (job.status === BackgroundJobStatusValue.Failed) {
    if (job.errorCount > 0) {
      return {
        variant: 'error',
        message: `${action} failed`,
        description: getFailedEntityDescription(job.type, failed),
      }
    }
    return {
      variant: 'error',
      message: `${action} failed`,
      description: BACKGROUND_JOB_GENERIC_ERROR_MESSAGE,
    }
  }

  if (job.status === BackgroundJobStatusValue.PartiallyCompleted) {
    return {
      variant: 'warning',
      message: joinPillCopySegments([
        `${action} finished with errors`,
        `${succeeded}/${job.totalCount.toLocaleString()}`,
      ]),
      description: getPartialFailureEntityDescription(job.type, failed),
    }
  }

  return {
    variant: 'success',
    message: joinPillCopySegments([
      `${action} complete`,
      getSuccessEntityCountLabel(job.type, succeeded),
    ]),
  }
}

export const getCancelledBarContent = (job: TrackedBackgroundJob): BackgroundJobBarContent => {
  const action = job.pillTerminalActionLabel
  const updated = job.successCount.toLocaleString()

  if (job.successCount > 0) {
    return {
      variant: 'cancelled',
      message: `${action} cancelled`,
      description: getCancelledPartialEntityDescription(job.type, updated),
    }
  }

  return {
    variant: 'cancelled',
    message: `${action} cancelled`,
    description: getCancelledNoUpdatesEntityDescription(job.type),
  }
}

/**
 * Terminal jobs eligible for the 60s auto-ack timer. Trackable completed pills (row has
 * viewId + recordTypeId, or session viewUuid) are excluded in BackgroundJobsProvider
 * until dismiss/navigate.
 */
export const shouldAutoDismissTerminalBackgroundJob = (
  job: Pick<TrackedBackgroundJob, 'isInTerminalStatus' | 'status'>
) =>
  job.isInTerminalStatus &&
  (job.status === BackgroundJobStatusValue.Completed ||
    job.status === BackgroundJobStatusValue.Cancelled)

export const getJobBarContent = (job: TrackedBackgroundJob): BackgroundJobBarContent => {
  if (isActiveBackgroundJobStatus(job.status)) {
    return getInProgressBarContent(job)
  }
  if (job.status === BackgroundJobStatusValue.Cancelled) {
    return getCancelledBarContent(job)
  }
  return getCompletionBarContent(job)
}

/** Lower number = worse outcome; drives aggregate variant when all jobs are terminal. */
const getTerminalPriority = (job: TrackedBackgroundJob): number => {
  if (job.status === BackgroundJobStatusValue.Failed) {
    return 0
  }
  if (job.status === BackgroundJobStatusValue.PartiallyCompleted) {
    return 1
  }
  if (job.status === BackgroundJobStatusValue.Cancelled) {
    return 2
  }
  return 3
}

/**
 * Aggregate pill copy when multiple trackable jobs are visible. With ≥2 jobs and any
 * in-progress row, show total count (`N bulk operations`) + spinner — per-job status
 * lives in the dropdown. All-terminal multi-job copy is `N bulk operations finished`
 * with variant from the worst terminal outcome.
 */
export const getAggregateBackgroundJobBarContent = (
  trackedJobs: TrackedBackgroundJob[]
): AggregateBackgroundJobBarContent | null => {
  if (trackedJobs.length === 0) {
    return null
  }

  const inProgressJobs = trackedJobs.filter((trackedJob) =>
    isActiveBackgroundJobStatus(trackedJob.status)
  )
  const isMulti = trackedJobs.length >= 2

  if (inProgressJobs.length > 0) {
    const primaryInProgressJob = inProgressJobs[0]
    if (primaryInProgressJob == null) {
      return null
    }

    const inProgressContent = getJobBarContent(primaryInProgressJob)
    return {
      variant: 'inProgress',
      message: isMulti ? `${trackedJobs.length} bulk operations` : inProgressContent.message,
      description: isMulti ? undefined : inProgressContent.description,
      showSpinner: true,
    }
  }

  const sortedTerminalJobs = [...trackedJobs].sort(
    (trackedJobA, trackedJobB) =>
      getTerminalPriority(trackedJobA) - getTerminalPriority(trackedJobB)
  )
  const primaryTerminalJob = sortedTerminalJobs[0]
  if (!primaryTerminalJob) {
    return null
  }

  const terminalContent = getJobBarContent(primaryTerminalJob)
  const singleTerminalMessage = getPillRowDisplayMessage({
    message: terminalContent.message,
    layout: 'pill',
    job: primaryTerminalJob,
  })

  return {
    variant: terminalContent.variant,
    message: isMulti ? `${trackedJobs.length} bulk operations finished` : singleTerminalMessage,
    description: isMulti ? undefined : terminalContent.description,
    showSpinner: false,
  }
}
